/* QuoteCompareShared.jsx — read-only view van een gedeelde offerte-vergelijking.
   Route: /vergelijking/:token
   Haalt data op via GET /api/quote-compare/share/{token} (geen PII).
*/

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, AlertTriangle, Sparkles } from "lucide-react";
import { apiClient } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Results } from "./QuoteCompare";

export default function QuoteCompareShared() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await apiClient.get(`/quote-compare/share/${token}`);
        if (!cancelled) {
          setData(r.data);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-white text-[#003049] font-sans flex flex-col">
      <header className="bg-white border-b border-[#E5E9EE] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center">
            <img src="/img/doctorsol-logo.svg" alt="DoctorSol" className="h-12 w-auto" draggable={false} />
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {loading && (
        <div className="flex-1 flex items-center justify-center py-24">
          <div className="flex items-center gap-3 text-[#52606E]">
            <Loader2 className="w-6 h-6 animate-spin text-[#FCBF49]" />
            <span>{t("quote_compare.share.loading")}</span>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="flex-1 flex items-center justify-center py-24 px-6">
          <div className="text-center max-w-md">
            <AlertTriangle className="w-10 h-10 text-[#D62828] mx-auto mb-4" />
            <p className="text-[#003049] mb-6">{t("quote_compare.share.not_found")}</p>
            <Link to="/offertes-vergelijken"
              className="inline-flex items-center gap-2 bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-6 py-3">
              {t("quote_compare.share.viewer_cta_button")}
            </Link>
          </div>
        </div>
      )}

      {data && !loading && (
        <>
          <section className="bg-[#FFFBEF] border-b border-[#FCBF49]/40 py-6" data-testid="qc-shared-banner">
            <div className="max-w-5xl mx-auto px-6 lg:px-10 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-[#FCBF49] shrink-0 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-[#FCBF49] mb-1">
                  {t("quote_compare.share.viewer_badge")}
                  {data.shared_by ? ` · ${t("quote_compare.share.viewer_shared_by", { name: data.shared_by })}` : ""}
                </div>
                <p className="text-sm text-[#003049] leading-relaxed">{t("quote_compare.share.viewer_subtitle")}</p>
              </div>
            </div>
          </section>
          <Results
            result={{ parsed: data.parsed, advisory: data.advisory, token }}
            sharedView={true}
          />
        </>
      )}

      <footer className="bg-[#003049] text-white py-10 mt-auto">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/70">
          <div>{t("quote_compare.footer.copy")}</div>
          <div className="flex gap-5">
            <Link to="/" className="hover:text-white">{t("quote_compare.footer.home")}</Link>
            <Link to="/faq" className="hover:text-white">{t("quote_compare.footer.faq")}</Link>
            <Link to="/over-ons" className="hover:text-white">{t("quote_compare.footer.about")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
